import { proethicaLiveSessionConfig } from "@constants/demo";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { getPlatform } from "@memorang/helpers";
import type { RecordMixedItemInput } from "@memorang/types";
import { trackAnswerEvent } from "../api/mutations";
import type { TrackAnswerEventRequest } from "../api/types";
import useSessionStore from "../hooks/useSessionStore";

// Selection falls back to the item's current local state so heartbeat/pause
// events (no `answers` payload) never clobber an already-recorded answer.
const resolveSelectedChoiceIds = (
	input: RecordMixedItemInput,
	itemId: number,
) =>
	input.answers?.selectedChoiceIds ??
	useSessionStore.getState().itemStates?.get(itemId)?.selectedChoiceIds ??
	[];

const recordSummativeTestEvents = async (
	input: RecordMixedItemInput,
): Promise<boolean> => {
	const state = useSessionStore.getState();
	// Only live ProEthica pre/post tests have a real backend session to record
	// against; any other summative flow keeps the historical no-op.
	if (!state.proethicaTestKind) {
		return true;
	}

	const itemId = Number(input.itemId);
	const item = state.sessionItems?.find(
		(sessionItem) => sessionItem.id === itemId,
	);
	const request: TrackAnswerEventRequest = {
		itemId,
		itemVersionId: item?.versionId || 1,
		sessionId: Number(input.sessionId),
		collectionId: state.collectionId!,
		userId: Number(input.userId),
		projectId: proethicaLiveSessionConfig.projectId,
		organizationId: proethicaLiveSessionConfig.organizationId,
		sessionType: "SUMMATIVE",
		itemDetails: {
			type: "ANSWER_EVENT",
			itemType: "QUESTION",
			variant: input.variant,
		},
		response: {
			selectedChoiceIds: resolveSelectedChoiceIds(input, itemId),
			...(input.answers && {
				confidence: input.answers.confidence,
				correctness: input.answers.score > 0,
			}),
		},
		performance: input.answers
			? {
					timeTaken: input.answers.timeTaken,
					score: input.answers.score,
					xp: 0,
					points: 0,
				}
			: {},
		settings: input.answers
			? { shuffled: false, numDistractors: input.answers.numDistractors }
			: {},
		userInteraction: {
			highlight: "",
			marked: false,
			sessionTime: input.sessionTime,
		},
		platform: getPlatform(),
	};

	try {
		await trackAnswerEvent(request);
		return true;
	} catch (error) {
		captureErrorToSentry(new Error("Error recording summative answer event"), {
			error: JSON.stringify(error),
			itemId: input.itemId,
			sessionId: input.sessionId,
		});
		return false;
	}
};

export { recordSummativeTestEvents };
